/**
 * Account-backed comments on a writing.
 *
 * Talks to /apps/portal/api/comments, which is the portal app behind the
 * Shopify app proxy. Identity is never sent from here: the proxy appends a
 * signed `logged_in_customer_id` on its way through Shopify, so the server
 * knows who is posting and the browser cannot claim to be anyone.
 *
 * Everything is built with createElement and textContent rather than innerHTML.
 * Comment bodies are arbitrary text written by other people; the one rule that
 * makes that safe is that they are never parsed as markup, and a single
 * innerHTML on this path would quietly undo it.
 */
(() => {
  const root = document.querySelector('[data-es-comments]');
  if (!root) return;

  const articleId = root.dataset.articleId;
  const API = '/apps/portal/api/comments';
  const PROFILE_API = '/apps/portal/api/profile';
  const SORTS = [
    { id: 'newest', label: 'Newest' },
    { id: 'oldest', label: 'Oldest' },
    { id: 'liked', label: 'Top' },
  ];

  let state = { comments: [], count: 0, viewer: null };
  let replyingTo = null;
  let sort = 'newest';
  let freshId = null;
  // Collapsed branches, by comment id. Deliberately not persisted: a thread
  // someone collapsed to read past is not a preference, and restoring it on a
  // later visit would hide replies they have never seen.
  const collapsed = new Set();

  /* ------------------------------------------------------------ helpers */

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const svg = (markup) => {
    // Icons are the one place markup is built from a string. The strings are
    // literals in this file, never anything that came off the network.
    const wrap = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    wrap.setAttribute('viewBox', '0 0 24 24');
    wrap.setAttribute('aria-hidden', 'true');
    wrap.setAttribute('fill', 'none');
    wrap.setAttribute('stroke', 'currentColor');
    wrap.setAttribute('stroke-width', '2');
    wrap.setAttribute('stroke-linecap', 'round');
    wrap.setAttribute('stroke-linejoin', 'round');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', markup);
    wrap.append(path);
    return wrap;
  };

  const HEART = 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1L12 21l7.7-7.6 1.1-1a5.5 5.5 0 0 0 0-7.8z';

  const when = (iso) => {
    const then = new Date(iso);
    const mins = Math.floor((Date.now() - then.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return then.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  /**
   * A stable colour per name. Same hashing idea as tools/vine/generate.mjs:
   * deterministic, so someone keeps their colour between visits and between
   * readers, without storing anything.
   */
  const hueFor = (name) => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (Math.imul(h, 31) + name.charCodeAt(i)) >>> 0;
    return h % 360;
  };

  const initials = (name) =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

  const api = async (options = {}) => {
    const response = await fetch(options.url ?? `${API}?article=${encodeURIComponent(articleId)}`, {
      method: options.method ?? 'GET',
      headers: options.body ? { 'Content-Type': 'application/json' } : undefined,
      body: options.body ? JSON.stringify(options.body) : undefined,
      credentials: 'same-origin',
    });
    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, status: response.status, data };
  };

  const countAll = (nodes) =>
    nodes.reduce((n, c) => n + (c.removed ? 0 : 1) + countAll(c.replies || []), 0);

  const sortTree = (nodes) => {
    const copy = [...nodes];
    if (sort === 'oldest') copy.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    else if (sort === 'liked') copy.sort((a, b) => (b.likes - a.likes) || (new Date(b.createdAt) - new Date(a.createdAt)));
    else copy.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    // Replies always read oldest-first: a conversation re-ordered by score
    // stops being followable.
    for (const n of copy) n.replies = [...(n.replies || [])].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    );
    return copy;
  };

  /* -------------------------------------------------------------- render */

  function render() {
    root.textContent = '';

    const head = el('div', 'es-comments__head');
    head.append(el('h2', 'es-comments__title', 'Conversation'));
    if (state.count) head.append(el('span', 'es-comments__tally', String(state.count)));
    if (state.comments.length > 1) head.append(sortControl());
    root.append(head);

    root.append(state.viewer?.signedIn ? composer() : signInPrompt());

    if (!state.comments.length) {
      root.append(el('p', 'es-comments__empty', 'No thoughts shared yet. Yours would be the first.'));
      return;
    }

    root.append(threadList(sortTree(state.comments)));
  }

  function sortControl() {
    const group = el('div', 'es-comments__sort');
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', 'Sort comments');
    for (const option of SORTS) {
      const button = el('button', 'es-comments__sort-option', option.label);
      button.type = 'button';
      button.setAttribute('aria-pressed', String(sort === option.id));
      button.addEventListener('click', () => { sort = option.id; render(); });
      group.append(button);
    }
    return group;
  }

  function signInPrompt() {
    const box = el('div', 'es-comments__signin');
    box.append(el('p', null, 'Sign in to join the conversation.'));
    const button = el('button', 'es-comments__button', 'Sign in');
    button.type = 'button';
    button.addEventListener('click', openSignIn);
    box.append(button);
    return box;
  }

  function openSignIn() {
    // Reuse the header's account panel rather than sending them to Shopify
    // from here: it prefills the email and returns to this page.
    const trigger = [...document.querySelectorAll('[data-account-trigger]')]
      .find((a) => { const b = a.getBoundingClientRect(); return b.width > 0 && b.left >= 0; });
    if (trigger) trigger.click();
    else window.location.assign('/apps/portal/account');
  }

  function composer(parentId = null) {
    const form = el('form', parentId ? 'es-comments__reply-form' : 'es-comments__composer');

    // contenteditable, not a textarea: the formatting has to be visible as
    // you type. What leaves here is still the plain-text subset --
    // ESRichText.serialize walks the DOM and understands six tags, and paste
    // is reinserted as plain text, so no foreign markup ever gets in.
    const field = el('div', 'es-comments__field');
    field.contentEditable = 'true';
    field.setAttribute('role', 'textbox');
    field.setAttribute('aria-multiline', 'true');
    field.dataset.placeholder = parentId ? 'Write a reply…' : 'Share a thought…';
    field.setAttribute('aria-label', parentId ? 'Write a reply' : 'Write a comment');
    field.addEventListener('paste', (e) => window.ESRichText?.handlePaste(e));

    const bar = toolbar(field);
    form.append(bar);
    form.append(field);

    const actions = el('div', 'es-comments__actions');
    const submit = el('button', 'es-comments__button', parentId ? 'Reply' : 'Post comment');
    submit.type = 'submit';
    actions.append(submit);

    if (parentId) {
      const cancel = el('button', 'es-comments__button es-comments__button--quiet', 'Cancel');
      cancel.type = 'button';
      cancel.addEventListener('click', () => { replyingTo = null; render(); });
      actions.append(cancel);
    }

    const counter = el('span', 'es-comments__counter', '');
    actions.append(counter);
    form.append(actions);

    const msg = el('p', 'es-comments__msg');
    msg.hidden = true;
    form.append(msg);

    const say = (text, tone) => {
      msg.textContent = text;
      msg.className = `es-comments__msg es-comments__msg--${tone}`;
      msg.hidden = false;
    };

    // Counted on the serialized text, which is what the server will measure.
    // The rendered length is different -- markers add characters nobody typed.
    const read = () =>
      window.ESRichText ? window.ESRichText.serialize(field) : field.textContent.trim();

    field.addEventListener('input', () => {
      const left = 4000 - read().length;
      counter.textContent = left < 500 ? `${left} left` : '';
      counter.className = `es-comments__counter${left < 0 ? ' es-comments__counter--over' : ''}`;
      if (!msg.hidden) msg.hidden = true;
      field.classList.toggle('es-comments__field--empty', field.textContent.trim() === '');
    });
    field.classList.add('es-comments__field--empty');

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const body = read();
      if (!body) { say('Write something first.', 'bad'); return; }
      if (body.length > 4000) { say('That is longer than 4000 characters.', 'bad'); return; }

      submit.disabled = true;
      try {
        // A name has to exist before the comment does, or the post is
        // rejected and the text they just wrote is at risk.
        if (!state.viewer.username) {
          const chosen = await chooseUsername();
          if (!chosen) { say('You need a user name before posting.', 'bad'); return; }
          state.viewer.username = chosen;
        }

        const result = await api({ method: 'POST', body: { articleId, parentId, body } });
        if (result.status === 401) { say('Your session ended. Sign in again to post.', 'bad'); return; }
        if (!result.ok) { say(result.data.error ?? 'That did not post. Try again.', 'bad'); return; }

        field.textContent = '';
        field.classList.add('es-comments__field--empty');
        replyingTo = null;
        freshId = result.data.id ?? null;
        await load();
      } catch {
        say('Could not reach the server. Try again.', 'bad');
      } finally {
        submit.disabled = false;
      }
    });

    return form;
  }

  const TOOLS = [
    { kind: 'bold', label: 'Bold', hint: 'Bold (**text**)', glyph: 'B', className: 'es-tool--bold' },
    { kind: 'italic', label: 'Italic', hint: 'Italic (_text_)', glyph: 'I', className: 'es-tool--italic' },
    { kind: 'quote', label: 'Quote', hint: 'Quote (> text)', glyph: '\u201C', className: 'es-tool--quote' },
  ];

  /**
   * Three buttons, deliberately plain. This is a comment box, not an editor:
   * the moderator's Quill toolbar in the portal is the place for a row of
   * controls, and reproducing it here would suggest a range of formatting
   * that comments do not actually support.
   */
  function toolbar(field) {
    const bar = el('div', 'es-comments__tools-bar');
    bar.setAttribute('role', 'group');
    bar.setAttribute('aria-label', 'Formatting');
    const buttons = [];

    for (const tool of TOOLS) {
      const button = el('button', `es-tool ${tool.className}`, tool.glyph);
      button.type = 'button';
      button.title = tool.hint;
      button.setAttribute('aria-label', tool.label);
      button.setAttribute('aria-pressed', 'false');
      // mousedown, not click: by click time the caret has already left the
      // editor and the selection the command needs is gone.
      button.addEventListener('mousedown', (event) => {
        event.preventDefault();
        window.ESRichText?.toggle(field, tool.kind);
        sync();
      });
      buttons.push([tool.kind, button]);
      bar.append(button);
    }

    // The buttons are toggles, so they have to say which formats the caret is
    // currently inside -- otherwise pressing B twice looks like nothing.
    const sync = () => {
      const active = window.ESRichText?.activeFormats(field) ?? {};
      for (const [kind, button] of buttons) {
        button.setAttribute('aria-pressed', String(Boolean(active[kind])));
      }
    };

    for (const type of ['keyup', 'mouseup', 'focus', 'input']) {
      field.addEventListener(type, sync);
    }
    document.addEventListener('selectionchange', () => {
      if (document.activeElement === field) sync();
    });

    return bar;
  }

  function threadList(nodes) {
    const list = el('ul', 'es-comments__list');
    for (const node of nodes) list.append(commentItem(node));
    return list;
  }

  // `depth` exists only to decide when "Replying to X" earns its place. Up to
  // the level where indentation stops (see the CSS), the nesting already says
  // who is being answered and the line is noise on every single reply.
  const FLAT_AT = 4;

  function commentItem(node, parentName = null, depth = 0) {
    const item = el('li', `es-comment${node.isModerator ? ' es-comment--team' : ''}` +
      (node.id === freshId ? ' es-comment--fresh' : ''));

    const main = el('div', 'es-comment__main');

    if (node.removed) {
      main.append(el('p', 'es-comment__body es-comment__body--gone',
        node.removedBy === 'moderator'
          ? 'This comment was removed by a moderator.'
          : 'This comment was removed.'));
      item.append(el('div', 'es-comment__avatar'), main);
    } else {
      const avatar = el('div', 'es-comment__avatar', initials(node.username));
      const hue = hueFor(node.username);
      avatar.style.background = node.isModerator
        ? 'var(--es-deep)'
        : `hsl(${hue} 34% 42%)`;
      item.append(avatar);

      if (parentName && depth >= FLAT_AT) {
        main.append(el('p', 'es-comment__replying', `Replying to ${parentName}`));
      }

      const head = el('div', 'es-comment__head');
      const author = el('span',
        `es-comment__author${node.isModerator ? ' es-comment__author--team' : ''}`,
        node.username);
      if (node.isModerator) {
        author.title = 'European Shaman team member';
        // The visual treatment is colour, weight and a wash. None of those
        // reach a screen reader, so the fact is stated in text too.
        const sr = el('span', 'visually-hidden', ' (European Shaman team member)');
        author.append(sr);
      }
      head.append(author);
      const time = el('span', 'es-comment__when', when(node.createdAt));
      time.title = new Date(node.createdAt).toLocaleString();
      head.append(time);
      main.append(head);

      // Never innerHTML: this is someone else's text. ESRichText turns the
      // four-marker subset into real elements built with createElement and
      // textContent, so nothing a commenter types is ever parsed as markup.
      // If the script failed to load, fall back to flat text rather than
      // reaching for a string.
      const body = el('div', 'es-comment__body');
      if (window.ESRichText) body.append(window.ESRichText.render(node.body));
      else body.textContent = node.body;
      main.append(body);

      const tools = el('div', 'es-comment__tools');
      tools.append(likeButton(node));

      if (state.viewer?.signedIn) {
        const reply = el('button', 'es-comments__button es-comments__button--quiet', 'Reply');
        reply.type = 'button';
        reply.addEventListener('click', () => {
          replyingTo = replyingTo === node.id ? null : node.id;
          render();
        });
        tools.append(reply);
      }

      if (node.canDelete) {
        const remove = el('button', 'es-comments__button es-comments__button--quiet',
          node.isOwn ? 'Delete' : 'Remove');
        remove.type = 'button';
        remove.addEventListener('click', async () => {
          const ok = window.confirm(
            node.isOwn ? 'Delete your comment?' : `Remove this comment by ${node.username}?`,
          );
          if (!ok) return;
          remove.disabled = true;
          const result = await api({ method: 'POST', body: { intent: 'delete', commentId: node.id } });
          if (result.ok) await load();
          else remove.disabled = false;
        });
        tools.append(remove);
      }

      main.append(tools);
      if (replyingTo === node.id) main.append(composer(node.id));
      item.append(main);
    }

    const replies = node.replies ?? [];
    if (replies.length) {
      const isCollapsed = collapsed.has(node.id);
      const total = countAll(replies);

      const rail = el('button', 'es-comment__collapse');
      rail.type = 'button';
      rail.setAttribute('aria-expanded', String(!isCollapsed));
      rail.setAttribute('aria-label',
        `${isCollapsed ? 'Expand' : 'Collapse'} ${total} ${total === 1 ? 'reply' : 'replies'}`);
      rail.addEventListener('click', () => {
        if (collapsed.has(node.id)) collapsed.delete(node.id);
        else collapsed.add(node.id);
        render();
      });
      item.append(rail);

      if (isCollapsed) {
        const expand = el('button', 'es-comment__expand',
          `Show ${total} ${total === 1 ? 'reply' : 'replies'}`);
        expand.type = 'button';
        expand.addEventListener('click', () => { collapsed.delete(node.id); render(); });
        item.append(expand);
      } else {
        const sub = el('ul', 'es-comment__replies');
        for (const child of replies) {
          sub.append(commentItem(child, node.removed ? null : node.username, depth + 1));
        }
        item.append(sub);
      }
    }

    return item;
  }

  function likeButton(node) {
    const button = el('button', 'es-comment__like');
    button.type = 'button';
    button.setAttribute('aria-pressed', String(node.likedByViewer));
    button.append(svg(HEART));
    const count = el('span', 'es-comment__like-count', node.likes ? String(node.likes) : '');
    button.append(count);

    if (!state.viewer?.signedIn) {
      button.setAttribute('aria-label', 'Sign in to like this comment');
      button.addEventListener('click', openSignIn);
      return button;
    }
    if (!node.canLike) {
      button.disabled = true;
      button.title = 'You cannot like your own comment';
      button.setAttribute('aria-label', `${node.likes} likes`);
      return button;
    }

    button.setAttribute('aria-label', node.likedByViewer ? 'Remove your like' : 'Like this comment');
    button.addEventListener('click', async () => {
      // Optimistic: the tap should feel instant. The server's number is
      // applied when it answers, and a failure puts the old one back rather
      // than leaving a count nobody else can see.
      const before = { liked: node.likedByViewer, likes: node.likes };
      node.likedByViewer = !before.liked;
      node.likes = before.likes + (node.likedByViewer ? 1 : -1);
      button.setAttribute('aria-pressed', String(node.likedByViewer));
      count.textContent = node.likes ? String(node.likes) : '';
      button.classList.toggle('es-comment__like--pop', node.likedByViewer);

      const result = await api({
        method: 'POST',
        body: { intent: 'like', commentId: node.id, liked: node.likedByViewer },
      });

      if (!result.ok) {
        node.likedByViewer = before.liked;
        node.likes = before.likes;
      } else {
        node.likedByViewer = result.data.liked;
        node.likes = result.data.likes;
      }
      button.setAttribute('aria-pressed', String(node.likedByViewer));
      count.textContent = node.likes ? String(node.likes) : '';
    });

    return button;
  }

  /* ------------------------------------------------------- name modal */

  /**
   * Ask for a display name in a modal with a live checker, resolving to the
   * chosen name or null if they backed out.
   *
   * The rules are fetched rather than written here, so the ticks report
   * exactly what the server will accept.
   */
  async function chooseUsername() {
    const profile = await api({ url: PROFILE_API });
    if (profile.ok && profile.data.username) return profile.data.username;

    const policy = profile.data?.policy ?? { rules: [], reserved: { exact: [], substring: [] } };
    const rules = policy.rules.map((r) => ({ ...r, re: new RegExp(r.pattern) }));
    const normalise = (v) => String(v || '').trim().replace(/\s+/g, ' ');

    const check = (raw) => {
      const value = normalise(raw);
      const results = rules.map((r) => ({ id: r.id, label: r.label, ok: r.re.test(value) }));
      const key = value.toLowerCase();
      const reserved = (policy.reserved.exact || []).includes(key) ||
        (policy.reserved.substring || []).some((w) => key.includes(w));
      return { value, results, reserved, ok: results.every((r) => r.ok) && !reserved };
    };

    const dialog = el('dialog', 'es-name');
    const body = el('div', 'es-name__body');
    body.append(el('h2', 'es-name__title', 'Choose your user name'));
    body.append(el('p', 'es-name__lede',
      'This is how you appear across the site. You can change it later in your account.'));

    const input = el('input', 'es-name__input');
    input.type = 'text';
    input.placeholder = 'e.g. Jackson K.';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.maxLength = policy.limits?.max ?? 24;
    input.setAttribute('aria-label', 'User name');
    body.append(input);

    const preview = el('p', 'es-name__preview');
    preview.hidden = true;
    body.append(preview);

    const list = el('ul', 'es-name__rules');
    body.append(list);

    const msg = el('p', 'es-name__msg');
    msg.hidden = true;
    body.append(msg);

    const actions = el('div', 'es-name__actions');
    const cancel = el('button', 'es-comments__button es-comments__button--quiet', 'Not now');
    cancel.type = 'button';
    const save = el('button', 'es-comments__button', 'Save name');
    save.type = 'button';
    save.disabled = true;
    actions.append(cancel, save);
    body.append(actions);
    dialog.append(body);
    document.body.append(dialog);

    const paint = () => {
      const st = check(input.value);
      const untouched = st.value.length === 0;
      list.textContent = '';
      for (const r of st.results) {
        const li = el('li', 'es-name__rule' + (untouched ? '' : r.ok ? ' es-name__rule--ok' : ' es-name__rule--bad'));
        const mark = el('span', 'es-name__rule-mark', untouched ? '·' : r.ok ? '✓' : '✕');
        mark.setAttribute('aria-hidden', 'true');
        li.append(mark, document.createTextNode(r.label));
        list.append(li);
      }
      if (st.reserved && !untouched) {
        const li = el('li', 'es-name__rule es-name__rule--bad');
        const mark = el('span', 'es-name__rule-mark', '✕');
        mark.setAttribute('aria-hidden', 'true');
        li.append(mark, document.createTextNode('That name is reserved'));
        list.append(li);
      }
      if (st.ok) {
        preview.textContent = '';
        preview.append(document.createTextNode('You will appear as '));
        preview.append(el('strong', null, st.value));
        preview.hidden = false;
      } else {
        preview.hidden = true;
      }
      save.disabled = !st.ok;
      return st;
    };

    paint();
    dialog.showModal();
    input.focus();

    return new Promise((resolve) => {
      let settled = null;

      input.addEventListener('input', () => { msg.hidden = true; paint(); });
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); save.click(); }
      });
      cancel.addEventListener('click', () => dialog.close());

      save.addEventListener('click', async () => {
        const st = paint();
        if (!st.ok) return;
        save.disabled = true;
        const result = await api({ url: PROFILE_API, method: 'POST', body: { username: st.value } });
        if (!result.ok) {
          msg.textContent = result.data.error ?? 'That name could not be used.';
          msg.hidden = false;
          save.disabled = false;
          return;
        }
        settled = result.data.username;
        dialog.close();
      });

      dialog.addEventListener('close', () => {
        dialog.remove();
        resolve(settled);
      });
    });
  }

  /* ---------------------------------------------------------------- boot */

  async function load() {
    const result = await api();
    if (!result.ok) {
      root.textContent = '';
      root.append(el('p', 'es-comments__empty', 'The conversation could not be loaded just now.'));
      return;
    }
    state = result.data;
    render();
    if (freshId) setTimeout(() => { freshId = null; }, 2600);
  }

  load();
})();
